/**
 * Logger Utility
 *
 * Centralized logging with configurable log levels.
 * Automatically disabled in production unless explicitly enabled.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerConfig {
  enabled: boolean;
  level: LogLevel;
}

const config: LoggerConfig = {
  enabled: import.meta.env.DEV || import.meta.env.VITE_ENABLE_LOGS === 'true',
  level: (import.meta.env.VITE_LOG_LEVEL as LogLevel | undefined) ?? 'info'
};

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

function shouldLog(level: LogLevel): boolean {
  if (!config.enabled) return false;
  return LOG_LEVELS[level] >= LOG_LEVELS[config.level];
}

class Logger {
  private context: string;

  constructor(context: string) {
    this.context = context;
  }

  debug(message: string, ...args: unknown[]): void {
    if (shouldLog('debug')) {
      // eslint-disable-next-line no-console
      console.log(`[${this.context}] ${message}`, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (shouldLog('info')) {
      // eslint-disable-next-line no-console
      console.log(`[${this.context}] ${message}`, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (shouldLog('warn')) {
      console.warn(`[${this.context}] ${message}`, ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (shouldLog('error')) {
      console.error(`[${this.context}] ${message}`, ...args);
    }
  }
}

/**
 * Create a logger instance for a specific context
 *
 * @param context - The context name (e.g., 'Ably', 'API', 'Room')
 * @returns Logger instance
 */
export function createLogger(context: string): Logger {
  return new Logger(context);
}

/**
 * Configure logger settings
 *
 * @param enabled - Enable/disable logging
 * @param level - Minimum log level to display
 */
export function configureLogger(enabled: boolean, level: LogLevel = 'info'): void {
  config.enabled = enabled;
  config.level = level;
}
